<template>
  <div class="fixed inset-0 z-50 overflow-y-auto wizard-modal blogcreate-wizard-modal">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 wizard-backdrop" @click="$emit('close')"></div>

      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 wizard-dialog">
        <div>
          <!-- Modal Header -->
          <div class="flex items-center justify-between mb-6">
            <div class="flex items-center">
              <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <div class="ml-4">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Create Blog Post
                </h3>
                <p class="text-sm text-gray-500">
                  Step {{ currentStep }} of {{ totalSteps }}: {{ stepTitles[currentStep - 1] }}
                </p>
              </div>
            </div>
            <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Progress Bar -->
          <div class="mb-8">
            <div class="flex items-center">
              <div v-for="step in totalSteps" :key="step" class="flex items-center">
                <div 
                  :class="[
                    'flex items-center justify-center w-8 h-8 rounded-full border-2',
                    step <= currentStep ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-500'
                  ]"
                >
                  <span class="text-sm font-medium">{{ step }}</span>
                </div>
                <div 
                  v-if="step < totalSteps"
                  :class="[
                    'flex-1 h-1 mx-4',
                    step < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                  ]"
                ></div>
              </div>
            </div>
          </div>

          <!-- Step Content -->
          <div class="mb-8">
            <!-- Step 1: Content Type -->
            <div v-if="currentStep === 1">
              <h4 class="text-lg font-medium text-gray-900 mb-4">How would you like to create your blog post?</h4>
              <div class="space-y-3">
                <label class="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50" 
                       :class="{ 'border-blue-500 bg-blue-50': blogData.contentType === 'existing' }">
                  <input
                    type="radio"
                    value="existing"
                    v-model="blogData.contentType"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div class="ml-3">
                    <div class="font-medium text-gray-900">📄 I have existing content</div>
                    <div class="text-sm text-gray-500">Paste or type your blog content</div>
                  </div>
                </label>
                <label class="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
                       :class="{ 'border-blue-500 bg-blue-50': blogData.contentType === 'ai' }">
                  <input
                    type="radio"
                    value="ai"
                    v-model="blogData.contentType"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <div class="ml-3">
                    <div class="font-medium text-gray-900">🤖 Generate with AI</div>
                    <div class="text-sm text-gray-500">AI will create content based on your requirements</div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Step 2: Basic Information -->
            <div v-if="currentStep === 2">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Blog Post Details</h4>
              <div class="space-y-4">
                <div>
                  <label for="title" class="block text-sm font-medium text-gray-700 mb-1">
                    Blog Title *
                  </label>
                  <input
                    id="title"
                    type="text"
                    v-model="blogData.title"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your blog post title"
                  />
                </div>
                <div>
                  <label for="category" class="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    id="category"
                    v-model="blogData.category"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a category</option>
                    <option value="news">News & Updates</option>
                    <option value="tips">Tips & Guides</option>
                    <option value="company">Company</option>
                    <option value="product">Product</option>
                    <option value="industry">Industry</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label for="tags" class="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <input
                    id="tags"
                    type="text"
                    v-model="blogData.tags"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g. web design, seo, marketing (optional)"
                  />
                  <p class="text-xs text-gray-500 mt-1">Separate tags with commas</p>
                </div>
              </div>
            </div>

            <!-- Step 3: Content -->
            <div v-if="currentStep === 3">
              <h4 class="text-lg font-medium text-gray-900 mb-4">
                {{ blogData.contentType === 'existing' ? 'Your Content' : 'AI Generation Parameters' }}
              </h4>
              
              <!-- Existing Content -->
              <div v-if="blogData.contentType === 'existing'" class="space-y-4">
                <div>
                  <label for="content" class="block text-sm font-medium text-gray-700 mb-1">
                    Blog Content *
                  </label>
                  <textarea
                    id="content"
                    v-model="blogData.content"
                    rows="12"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Paste or type your blog content here..."
                  ></textarea>
                  <p class="text-xs text-gray-500 mt-1">Supports Markdown formatting</p>
                </div>
              </div>

              <!-- AI Generation Parameters -->
              <div v-if="blogData.contentType === 'ai'" class="space-y-4">
                <div>
                  <label for="topic" class="block text-sm font-medium text-gray-700 mb-1">
                    Topic/Subject *
                  </label>
                  <input
                    id="topic"
                    type="text"
                    v-model="blogData.aiParams.topic"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g. How to improve website performance"
                  />
                </div>
                <div>
                  <label for="audience" class="block text-sm font-medium text-gray-700 mb-1">
                    Target Audience
                  </label>
                  <input
                    id="audience"
                    type="text"
                    v-model="blogData.aiParams.audience"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g. Small business owners"
                  />
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label for="tone" class="block text-sm font-medium text-gray-700 mb-1">
                      Tone
                    </label>
                    <select
                      id="tone"
                      v-model="blogData.aiParams.tone"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="professional">Professional</option>
                      <option value="casual">Casual</option>
                      <option value="friendly">Friendly</option>
                      <option value="authoritative">Authoritative</option>
                      <option value="conversational">Conversational</option>
                    </select>
                  </div>
                  <div>
                    <label for="length" class="block text-sm font-medium text-gray-700 mb-1">
                      Length
                    </label>
                    <select
                      id="length"
                      v-model="blogData.aiParams.length"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="short">Short (400-600 words)</option>
                      <option value="medium">Medium (800-1000 words)</option>
                      <option value="long">Long (1200-1500 words)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label for="keyPoints" class="block text-sm font-medium text-gray-700 mb-1">
                    Key Points to Cover
                  </label>
                  <textarea
                    id="keyPoints"
                    v-model="blogData.aiParams.keyPoints"
                    rows="4"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="List the main points or topics you want covered in the blog post..."
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Step 4: Preview -->
            <div v-if="currentStep === 4">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Review Your Blog Post</h4>
              <div class="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto">
                <div class="space-y-4">
                  <div>
                    <span class="text-sm font-medium text-gray-500">Title:</span>
                    <h5 class="text-xl font-bold text-gray-900">{{ blogData.title }}</h5>
                  </div>
                  <div v-if="blogData.category" class="flex items-center space-x-4">
                    <div>
                      <span class="text-sm font-medium text-gray-500">Category:</span>
                      <span class="ml-1 text-sm text-gray-900 capitalize">{{ blogData.category }}</span>
                    </div>
                    <div v-if="blogData.tags">
                      <span class="text-sm font-medium text-gray-500">Tags:</span>
                      <span class="ml-1 text-sm text-gray-900">{{ blogData.tags }}</span>
                    </div>
                  </div>
                  <div class="border-t pt-4">
                    <span class="text-sm font-medium text-gray-500">Content Preview:</span>
                    <div class="mt-2 prose prose-sm max-w-none">
                      <div v-if="blogData.contentType === 'existing'" class="whitespace-pre-wrap">{{ contentPreview }}</div>
                      <div v-else class="italic text-gray-600">
                        AI will generate {{ blogData.aiParams.length }} content about "{{ blogData.aiParams.topic }}" 
                        in a {{ blogData.aiParams.tone }} tone
                        <span v-if="blogData.aiParams.audience"> for {{ blogData.aiParams.audience }}</span>.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation Buttons -->
          <div class="flex justify-between">
            <button
              v-if="currentStep > 1"
              @click="previousStep"
              class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div v-else></div>

            <div class="space-x-3">
              <button
                @click="$emit('close')"
                class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                v-if="currentStep < totalSteps"
                @click="nextStep"
                :disabled="!canProceed"
                class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                v-else
                @click="createBlogPost"
                :disabled="!canProceed"
                class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isSubmitting" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                {{ isSubmitting ? 'Creating...' : 'Create Blog Post' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive } from 'vue'

export default {
  name: 'BlogCreateModal',
  emits: ['close', 'create'],
  setup(props, { emit }) {
    const currentStep = ref(1)
    const totalSteps = 4
    const isSubmitting = ref(false)
    
    const stepTitles = [
      'Content Type',
      'Basic Information', 
      'Content Details',
      'Review & Create'
    ]

    const blogData = reactive({
      contentType: '',
      title: '',
      category: '',
      tags: '',
      content: '',
      aiParams: {
        topic: '',
        audience: '',
        tone: 'professional',
        length: 'medium',
        keyPoints: ''
      }
    })

    const canProceed = computed(() => {
      switch (currentStep.value) {
        case 1:
          return blogData.contentType !== ''
        case 2:
          return blogData.title.trim() !== ''
        case 3:
          if (blogData.contentType === 'existing') {
            return blogData.content.trim() !== ''
          } else {
            return blogData.aiParams.topic.trim() !== ''
          }
        case 4:
          return true
        default:
          return false
      }
    })

    const contentPreview = computed(() => {
      if (blogData.content.length > 300) {
        return blogData.content.substring(0, 300) + '...'
      }
      return blogData.content
    })

    const nextStep = () => {
      if (currentStep.value < totalSteps && canProceed.value) {
        currentStep.value++
      }
    }

    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }

    const createBlogPost = () => {
      if (!canProceed.value) return
      
      isSubmitting.value = true
      
      // Generate the prompt based on content type
      let prompt = `Add a new blog post to the website with the following details:\n\n`
      prompt += `Title: ${blogData.title}\n`
      
      if (blogData.category) {
        prompt += `Category: ${blogData.category}\n`
      }
      
      if (blogData.tags) {
        prompt += `Tags: ${blogData.tags}\n`
      }
      
      prompt += `\n`
      
      if (blogData.contentType === 'existing') {
        prompt += `Content:\n${blogData.content}\n\n`
        prompt += `Please format this as a professional blog post with proper headings, paragraphs, and add it to the website.`
      } else {
        prompt += `Please generate a blog post with the following specifications:\n`
        prompt += `- Topic: ${blogData.aiParams.topic}\n`
        if (blogData.aiParams.audience) {
          prompt += `- Target audience: ${blogData.aiParams.audience}\n`
        }
        prompt += `- Tone: ${blogData.aiParams.tone}\n`
        prompt += `- Length: ${blogData.aiParams.length}\n`
        if (blogData.aiParams.keyPoints) {
          prompt += `- Key points to cover: ${blogData.aiParams.keyPoints}\n`
        }
        prompt += `\nPlease create engaging, well-structured content with proper headings and paragraphs, then add it to the website.`
      }

      // Emit the create event with the generated prompt
      emit('create', { prompt, blogData: { ...blogData } })
    }

    return {
      currentStep,
      totalSteps,
      stepTitles,
      blogData,
      canProceed,
      contentPreview,
      isSubmitting,
      nextStep,
      previousStep,
      createBlogPost
    }
  }
}
</script>
<style src="./wizard-glass-modal.css"></style>

